import React, { useState, useCallback, useEffect } from 'react';
import './SearchTab.css';
import SearchContent from './SearchContent';
import SearchHeader from './SearchHeader';
import { performSearch } from '../../../api/searchServices';
import { SearchResponse } from '../../../types/searchType';
import debounce from 'lodash/debounce';
import SearchResults from './SearchResults';
import { UserType } from '../../../types/userType';
import { useLocation, useNavigate } from 'react-router-dom';

interface SearchTabProps {
  isActive: boolean;
  currentUser: UserType | null;
}

export type SearchState = 'initial' | 'focused' | 'typing' | 'results';
export type ResultTab = 'posts' | 'brands';

const SearchTab: React.FC<SearchTabProps> = ({ isActive, currentUser }) => {
  // Move state initialization outside of useState
  const savedState = localStorage.getItem('searchTabState');
  const initialState = savedState ? JSON.parse(savedState) : null;

  const [searchQuery, setSearchQuery] = useState(initialState?.query || '');
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(initialState?.results || null);
  const [searchState, setSearchState] = useState<SearchState>(initialState?.state || 'initial');
  const [activeResultTab, setActiveResultTab] = useState<ResultTab>('posts');
  const [isLoading, setIsLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Separate the localStorage effect from state changes
  useEffect(() => {
    if (!isActive) return; // Don't update localStorage if tab is not active

    if (searchState !== 'initial') {
      localStorage.setItem('searchTabState', JSON.stringify({
        state: searchState,
        query: searchQuery,
        results: searchResults
      }));
    } else {
      localStorage.removeItem('searchTabState');
    }
  }, [isActive, searchState, searchQuery, searchResults]);

  // Handle location state changes in a separate effect
  useEffect(() => {
    if (!isActive) return; // Don't process location state if tab is not active

    const locationState = location.state;
    if (locationState) {
      const { searchState: newSearchState, searchQuery: newQuery, fromHashtag } = locationState;
      
      if (newSearchState) setSearchState(newSearchState);
      if (newQuery) {
        setSearchQuery(newQuery);
        if (fromHashtag) {
          handleSearch(newQuery);
        }
      }
      
      // Clear location state
      navigate(location.pathname, { replace: true });
    }
  }, [isActive, location, navigate]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query) {
        try {
          setIsLoading(true);
          const results = await performSearch(query);
          console.log('SearchTab - API Response:', results);
          setSearchResults(results);
          setSearchState('results');
          document.activeElement instanceof HTMLElement && document.activeElement.blur();
        } catch (error) {
          console.error('Error fetching search results:', error);
        } finally {
          setIsLoading(false);
        }
      }
    }, 300),
    []
  );

  // This effect handles real-time typing search
  useEffect(() => {
    if (searchQuery && searchState === 'typing') {
      debouncedSearch(searchQuery);
    } else if (!searchQuery) {
      setSearchState('focused');
      setSearchResults(null);
    }
  }, [searchQuery, debouncedSearch]);

  // This function handles direct keyword clicks
  const handleSearch = useCallback(async (query: string) => {
    if (query.trim().length === 0) {
      setSearchState('initial');
      setSearchResults(null);
      localStorage.removeItem('searchTabState');
      return;
    }

    setSearchQuery(query);
    setIsLoading(true);
    setSearchState('results');  // Set to results for direct keyword clicks

    try {
      const results = await performSearch(query);
      setSearchResults(results);
      document.activeElement instanceof HTMLElement && document.activeElement.blur();
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onSearchFocus = () => {
    setSearchState('focused');
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value) {
      setSearchState('typing');
    } else {
      setSearchState('focused');
    }
  };

  const onCancelSearch = () => {
    setSearchQuery('');
    setSearchState('initial');
    setSearchResults(null);
    localStorage.removeItem('searchTabState');
  };

  const onSearchSubmit = () => {
    if (searchQuery) {
      debouncedSearch(searchQuery);
    }
  };

  if (!isActive) return null;

  return (
    <div className={`search-tab ${searchState !== 'initial' ? 'search-focused' : ''}`}>
      <SearchHeader
        searchQuery={searchQuery}
        onSearchFocus={onSearchFocus}
        onSearchChange={onSearchChange}
        onCancelSearch={onCancelSearch}
        searchState={searchState}
        onSearchSubmit={onSearchSubmit}
      />
      <SearchContent
        searchState={searchState}
        searchQuery={searchQuery}
        activeResultTab={activeResultTab}
        setActiveResultTab={setActiveResultTab}
        searchResults={searchResults}
        isLoading={isLoading}
        currentUser={currentUser}
        onSearch={handleSearch}
      />
    </div>
  );
};






// Keep the dummyAccounts array as is

export default SearchTab;
